import { getStripe } from './stripe'

/**
 * Finishing a card payment the server has already confirmed with Stripe
 * (backend services/payment_actions.py). Most charges are done when the
 * server answers; when the bank wants the cardholder to approve it (3-D
 * Secure, required for many UK and EU cards) the response carries
 * `payment_status: "requires_action"` and a `client_secret`, and Stripe.js
 * shows the bank's approval step here.
 *
 * What the buyer gets (a tip counted, a package opened, a subscription's
 * access) follows Stripe's webhook on the server, never this result: an
 * approved payment shows up there within moments.
 */

export interface ChargeOutcome {
  payment_status?: string | null
  client_secret?: string | null
}

export type CardPaymentResult =
  | { status: 'paid' }
  /** Approved; the bank is still settling it. */
  | { status: 'processing' }
  | { status: 'failed'; message: string }

export const APPROVAL_UNAVAILABLE =
  'Your bank needs to approve this payment, but the approval step could not open. Please try again.'

export const completeCardPayment = async (charge: ChargeOutcome): Promise<CardPaymentResult> => {
  if (charge.payment_status === 'processing') return { status: 'processing' }
  if (charge.payment_status !== 'requires_action') return { status: 'paid' }
  if (!charge.client_secret) return { status: 'failed', message: APPROVAL_UNAVAILABLE }

  const stripe = await getStripe()
  if (!stripe) return { status: 'failed', message: APPROVAL_UNAVAILABLE }

  const { paymentIntent, error } = await stripe.handleNextAction({ clientSecret: charge.client_secret })
  if (error) {
    return { status: 'failed', message: error.message || 'Your bank did not approve this payment.' }
  }
  if (paymentIntent?.status === 'succeeded') return { status: 'paid' }
  if (paymentIntent?.status === 'processing') return { status: 'processing' }
  return { status: 'failed', message: 'Your bank did not approve this payment.' }
}
